import { api } from "@/modules/api";
import { updateAudioRecordingTranscript } from "@/modules/audio-recording/update-transcript";
import { logger } from "@/modules/logger";
import type { Uuid } from "@/domains/global/identifiers";

const DEFAULT_RETRANSCRIBE_ERROR_MESSAGE = "Retranscribe request failed. Please try again.";

type RetranscribeFromSourceArgs = {
  audioRecordingId: Uuid;
  sourceBlobName: string;
};

type RetranscribeFromSourceResult =
  | { success: true; transcriptContent: string }
  | { success: false; errorMessage: string };

const extractErrorMessage = (rawError: unknown): string => {
  if (!rawError || typeof rawError !== "object") {
    return DEFAULT_RETRANSCRIBE_ERROR_MESSAGE;
  }

  const errorWithMessage = rawError as { error_message?: unknown; detail?: unknown };

  if (typeof errorWithMessage.error_message === "string") {
    return errorWithMessage.error_message;
  }

  if (typeof errorWithMessage.detail === "string") {
    return errorWithMessage.detail;
  }

  return DEFAULT_RETRANSCRIBE_ERROR_MESSAGE;
};

/**
 * Calls the backend to retranscribe an audio recording from its uploaded source.
 * This is a synchronous call - the backend waits for the Temporal workflow to complete
 * and returns the transcript content directly.
 */
export async function retranscribeFromSource({
  audioRecordingId,
  sourceBlobName,
}: RetranscribeFromSourceArgs): Promise<RetranscribeFromSourceResult> {
  try {
    const response = await api.post(
      "/v2/audio-recordings/{audio_recording_id}/transcribe-from-source",
      {
        params: { path: { audio_recording_id: audioRecordingId } },
        body: { source_blob_name: sourceBlobName },
      }
    );

    if (!response.response.ok || !response.data) {
      const errorMessage = extractErrorMessage(response.error);
      logger.warn({
        message: "[retranscribeFromSource] API call failed",
        info: { audioRecordingId, errorMessage },
      });
      return { success: false, errorMessage };
    }

    const transcriptContent = response.data.transcript_content;
    if (!transcriptContent || !transcriptContent.trim()) {
      return {
        success: false,
        errorMessage: "No transcript available",
      };
    }

    await updateAudioRecordingTranscript({
      audioRecordingId,
      transcriptText: transcriptContent,
    });

    return {
      success: true,
      transcriptContent,
    };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : DEFAULT_RETRANSCRIBE_ERROR_MESSAGE;
    logger.error({
      message: "[retranscribeFromSource] Request failed",
      info: { audioRecordingId, error: String(error) },
    });
    return { success: false, errorMessage };
  }
}
